/**
 * RevenueTicker — Phase 1 simple version với 3 chỉ số:
 *   - Doanh thu hôm nay (today_revenue) — số to.
 *   - Doanh thu ca này (session_revenue).
 *   - Quỹ quán (shop_balance).
 *
 * KHÔNG có today_profit (Phase 1 chưa hiển thị) + KHÔNG có toggle
 * compact/expanded (per Phụ lục E whitelist).
 *
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-09.
 *   - `design/7. Thiết kế RevenueTicker.png` (bố cục tham khảo).
 */
import { BeanCounter } from "./BeanCounter";
import { formatBean, formatPct } from "../lib/format";

interface Props {
  sessionRevenue: number;
  todayRevenue: number;
  shopBalance: number;
  yesterdayDeltaPct: number | null;
  todayOrders: number;
}

export function RevenueTicker({
  sessionRevenue,
  todayRevenue,
  shopBalance,
  yesterdayDeltaPct,
  todayOrders,
}: Props) {
  const deltaIcon =
    yesterdayDeltaPct === null
      ? ""
      : yesterdayDeltaPct > 0
        ? "↑"
        : yesterdayDeltaPct < 0
          ? "↓"
          : "→";

  const deltaColor =
    yesterdayDeltaPct === null
      ? "text-coffee/60"
      : yesterdayDeltaPct > 0
        ? "text-leaf"
        : yesterdayDeltaPct < 0
          ? "text-stool"
          : "text-coffee/60";

  return (
    <div
      data-testid="revenue-ticker"
      className="bg-white/90 rounded-xl p-3 shadow-soft-amber border border-amber50/30"
    >
      <div className="text-xs text-coffee/60 mb-1">Doanh thu hôm nay</div>
      <div className="flex items-baseline justify-between gap-2">
        <BeanCounter value={todayRevenue} size="large" testId="revenue-today" />
        {yesterdayDeltaPct !== null && (
          <div
            data-testid="revenue-delta"
            className={`text-sm font-semibold ${deltaColor}`}
          >
            {deltaIcon} {formatPct(Math.abs(yesterdayDeltaPct))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
        <div>
          <div className="text-coffee/60">Ca này</div>
          <div className="font-semibold" data-testid="revenue-session">
            ☕ {formatBean(sessionRevenue)}
          </div>
        </div>
        <div>
          <div className="text-coffee/60">Quỹ</div>
          <div className="font-semibold" data-testid="revenue-balance">
            ☕ {formatBean(shopBalance)}
          </div>
        </div>
      </div>

      <div className="text-xs text-coffee/50 mt-2">
        {todayOrders} đơn hôm nay
      </div>
    </div>
  );
}
